import { canStylistAccessRoute } from "@/utilities/dashboardAccess";
import {
  IconBuildingBank,
  IconWallet,
  IconBuildingStore,
  IconCalendar,
  IconCashBanknote,
  IconChartBar,
  IconChartPie,
  IconClockHour4,
  IconFileInvoice,
  IconFilePlus,
  IconMessage,
  IconMoodSmile,
  IconQrcode,
  IconReceipt2,
  IconScissors,
  IconSpeakerphone,
  IconStar,
  IconUsers,
} from "@tabler/icons-react";

export type HomeRole = "manager" | "stylist";
// Tile colour family; the same group keeps the same colour on home and on the menu tab.
export type SectionGroup = "sales" | "people" | "team" | "salon";
export type SectionBadge = "onlineBookings" | "pendingReviews";
export type SectionIcon = typeof IconCalendar;

export type HomeSection = {
  id: string;
  label: string;
  to: string;
  icon: SectionIcon;
  group: SectionGroup;
  roles: readonly HomeRole[];
  badge?: SectionBadge;
  needsReviewAccess?: boolean;
  // Shown to a stylist only when they rent a chair.
  renterOnlyForStylist?: boolean;
  onOpen?: () => void;
};

const everyone = ["manager", "stylist"] as const;
const managers = ["manager"] as const;

export const homeSections: HomeSection[] = [
  { id: "calendar", label: "تقویم کاری", to: "/dashboard/calendar", icon: IconCalendar, group: "sales", roles: everyone },
  { id: "onlineBookings", label: "نوبت‌های آنلاین", to: "/dashboard/appointmentDeposit", icon: IconReceipt2, group: "sales", roles: everyone, badge: "onlineBookings" },
  { id: "addInvoice", label: "ثبت فاکتور", to: "/dashboard/payments/add", icon: IconFilePlus, group: "sales", roles: everyone },
  { id: "invoices", label: "فاکتورها", to: "/dashboard/invoices", icon: IconFileInvoice, group: "sales", roles: everyone },
  { id: "customers", label: "مشتریان", to: "/dashboard/customers", icon: IconMoodSmile, group: "people", roles: everyone },
  { id: "marketing", label: "بازاریابی", to: "/dashboard/marketing", icon: IconSpeakerphone, group: "people", roles: everyone },
  { id: "reviews", label: "نظرات مشتریان", to: "/dashboard/reviews", icon: IconStar, group: "people", roles: managers, badge: "pendingReviews", needsReviewAccess: true },
  { id: "smsCredit", label: "اعتبار پیامک", to: "/dashboard/sms-credit", icon: IconMessage, group: "people", roles: managers },
  { id: "teamMembers", label: "تیم", to: "/dashboard/teams/teamMembers", icon: IconUsers, group: "team", roles: managers },
  { id: "teamStats", label: "آمار تیم", to: "/dashboard/barber-overview", icon: IconChartBar, group: "team", roles: managers },
  { id: "myReport", label: "گزارش من", to: "/dashboard/barber-overview", icon: IconChartBar, group: "team", roles: ["stylist"] },
  { id: "shifts", label: "شیفت کاری", to: "/dashboard/teams/shiftsSchedule", icon: IconClockHour4, group: "team", roles: managers },
  { id: "finance", label: "امور مالی", to: "/dashboard/finance", icon: IconWallet, group: "salon", roles: managers },
  { id: "padixSettlements", label: "تسویه‌های پدیکس", to: "/dashboard/padix-settlements", icon: IconBuildingBank, group: "salon", roles: everyone, renterOnlyForStylist: true },
  { id: "settlements", label: "تسویه تیم", to: "/dashboard/settlements/employees", icon: IconCashBanknote, group: "team", roles: managers },
  { id: "reports", label: "گزارش‌ها", to: "/dashboard/overview", icon: IconChartPie, group: "salon", roles: managers },
  // The services page opens the category the previous visit left selected unless told otherwise.
  { id: "services", label: "خدمات", to: "/dashboard/schedule/services", icon: IconScissors, group: "salon", roles: managers, onOpen: () => localStorage.setItem("schedule-services-category", "all") },
  { id: "salonInfo", label: "اطلاعات مجموعه", to: "/dashboard/profile/edit", icon: IconBuildingStore, group: "salon", roles: managers },
  { id: "siteQr", label: "کیوآر کد سایت", to: "/dashboard/profile/edit/siteQrCode", icon: IconQrcode, group: "salon", roles: managers },
];

export const sectionGroups: { id: SectionGroup; label: Record<HomeRole, string> }[] = [
  { id: "sales", label: { manager: "نوبت و فروش", stylist: "نوبت و فروش" } },
  { id: "people", label: { manager: "مشتریان و بازاریابی", stylist: "مشتریان و بازاریابی" } },
  { id: "team", label: { manager: "تیم", stylist: "کارکرد من" } },
  { id: "salon", label: { manager: "مجموعه", stylist: "مجموعه" } },
];

export const badgeNouns: Record<SectionBadge, string> = {
  onlineBookings: "نوبت آنلاین جدید",
  pendingReviews: "نظر در انتظار بررسی",
};

export const MAX_SHORTCUTS = 7;

export const defaultShortcuts: Record<HomeRole, string[]> = {
  manager: ["addInvoice", "onlineBookings", "invoices", "marketing", "reviews", "shifts", "services"],
  stylist: ["addInvoice", "onlineBookings", "invoices", "customers", "marketing", "myReport", "calendar"],
};

// Fills the default row when one of the defaults is not available (reviews switched off, for example).
export const shortcutFallback: Record<HomeRole, string[]> = {
  manager: ["customers", "teamMembers", "smsCredit", "reports", "settlements", "teamStats", "salonInfo", "siteQr", "calendar"],
  stylist: [],
};

export function sectionsFor(role: HomeRole, canReadReviews: boolean, isRenter = false) {
  return homeSections.filter(
    (section) =>
      section.roles.includes(role) &&
      (!section.needsReviewAccess || canReadReviews) &&
      (role === "manager" || !section.renterOnlyForStylist || isRenter) &&
      (role === "manager" || canStylistAccessRoute(section.to)),
  );
}
